'use client'

import React, { useEffect, useId, useRef } from 'react'
import { createPortal } from 'react-dom'

import { cn } from '@/utilities/ui'

type FocusableElement = HTMLElement & { disabled?: boolean }

function getFocusableElements(container: HTMLElement): FocusableElement[] {
  return Array.from(
    container.querySelectorAll<FocusableElement>(
      'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])',
    ),
  ).filter((element) => !element.hasAttribute('disabled') && element.tabIndex !== -1)
}

type AccessibleDialogProps = {
  ariaLabel: string
  children: React.ReactNode
  className?: string
  contentClassName?: string
  initialFocusRef?: React.RefObject<HTMLElement | null>
  onClose: () => void
  open: boolean
  restoreFocusRef?: React.RefObject<HTMLElement | null>
}

export function AccessibleDialog({
  ariaLabel,
  children,
  className,
  contentClassName,
  initialFocusRef,
  onClose,
  open,
  restoreFocusRef,
}: AccessibleDialogProps) {
  const titleId = useId()
  const contentRef = useRef<HTMLDivElement>(null)
  const restoreFocusElementRef = useRef<HTMLElement | null>(null)

  useEffect(() => {
    if (!open) return

    restoreFocusElementRef.current =
      restoreFocusRef?.current ?? (document.activeElement as HTMLElement | null)

    const previousOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'

    const focusTarget = initialFocusRef?.current ?? contentRef.current
    focusTarget?.focus({ preventScroll: true })

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault()
        onClose()
        return
      }

      if (event.key !== 'Tab' || !contentRef.current) return

      const focusable = getFocusableElements(contentRef.current)
      if (focusable.length === 0) return

      const first = focusable[0]
      const last = focusable[focusable.length - 1]
      const active = document.activeElement

      if (event.shiftKey) {
        if (active === first || !contentRef.current.contains(active)) {
          event.preventDefault()
          last.focus()
        }
        return
      }

      if (active === last) {
        event.preventDefault()
        first.focus()
      }
    }

    window.addEventListener('keydown', handleKeyDown)

    return () => {
      document.body.style.overflow = previousOverflow
      window.removeEventListener('keydown', handleKeyDown)
      restoreFocusElementRef.current?.focus({ preventScroll: true })
    }
  }, [initialFocusRef, onClose, open, restoreFocusRef])

  if (!open) return null

  return createPortal(
    <div
      aria-labelledby={titleId}
      aria-modal="true"
      className={cn('fixed inset-0 z-[200] flex items-center justify-center bg-black/80 p-4', className)}
      onClick={onClose}
      role="dialog"
    >
      <div
        ref={contentRef}
        className={cn(
          'relative w-full max-h-[calc(100dvh-2rem)] max-w-[calc(100vw-2rem)] outline-none',
          contentClassName,
        )}
        onClick={(event) => event.stopPropagation()}
        tabIndex={-1}
      >
        <h2 className="sr-only" id={titleId}>
          {ariaLabel}
        </h2>
        {children}
      </div>
    </div>,
    document.body,
  )
}
